import ReportTimeReports from "../../core/model2/report/reports/time-reports/table/time-reports-table"
import {generateUniqueId} from "../../core/gd/utils/misc/generate-unique-id"
import {TimeReportTable_defaultAppSettings} from "../../views/reports/time-reports/reports-table/table/settings"
import {GroupingType} from "../../core/gd/manager/group-by/@types"
import {SystemColumnType} from "../../core/gd-modules/manager/columns/types"
import {AggregateFunctionType} from "../../core/data-structures/aggregators/types"

type ReportOptions = {
    disableUsersFilter?: boolean
}

function makeReport(
    name            : string,
    tableSettings   : object,
    settings?       : JSONObject | (() => JSONObject),
    options?        : ReportOptions
): () => ReportTimeReports {
    return () => {
        const report = new ReportTimeReports({
            id           : generateUniqueId(),
            name         : name,
            autoGenerate : false,
            settings     : typeof settings === 'function' ? settings() : settings
        })
        report.defaultTableSettings = tableSettings

        if (options?.disableUsersFilter) {
            report.disableUsersFilter = true
        }
        return report
    }
}

export const timeReportingPresets = {

    timeSheets: makeReport('Time sheets', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                  : SystemColumnType.TIME_REPORTED,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id                  : SystemColumnType.REPORTED_BY,
                aggregateFunction   : AggregateFunctionType.USERS_UNION
            },
            {
                id: SystemColumnType.REPORT_DATE
            },
            {
                id: SystemColumnType.REPORT_MESSAGE
            },
        ],
        grouping: {
            mode: GroupingType.NO_GROUPING
        },
    }),

    byProject: makeReport('Time reports by project', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                : SystemColumnType.TIME_REPORTED,
                aggregateFunction : AggregateFunctionType.SUM
            },
            {
                id                : SystemColumnType.REPORTED_BY,
                aggregateFunction : AggregateFunctionType.USERS_UNION
            },
            {
                id                : SystemColumnType.ESTIMATE,
                aggregateFunction : AggregateFunctionType.SUM
            },
            {
                id                : SystemColumnType.SPENT_ESTIMATE,
                aggregateFunction : AggregateFunctionType.TOTAL_SPENT_ESTIMATE
            }
        ],
        grouping: {
            mode    : GroupingType.HIERARCHY,
        },
    }),

    byUser: makeReport('Time reports by user', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                  : SystemColumnType.TIME_REPORTED,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id: SystemColumnType.REPORT_DATE
            },
            {
                id: SystemColumnType.REPORT_MESSAGE
            }
        ],
        grouping: {
            mode    : GroupingType.CUSTOM,
            fields  : [
                {
                    fieldId         : 'userId',
                    sortDirection   : 'asc',
                    params          : {}
                },
                {
                    fieldId         : 'entity',
                    sortDirection   : 'asc',
                    params          : {}
                }
            ]
        },
    }),

    byDate: makeReport('Time reports by date', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                : SystemColumnType.TIME_REPORTED,
                aggregateFunction : AggregateFunctionType.SUM
            },
            {
                id                : SystemColumnType.REPORTED_BY,
                aggregateFunction : AggregateFunctionType.USERS_UNION
            },
            {
                id: SystemColumnType.REPORT_WORK_ITEM
            },
            {
                id: SystemColumnType.REPORT_MESSAGE
            }
        ],
        grouping: {
            mode: GroupingType.CUSTOM,
            fields: [
                {
                    fieldId         : 'date',
                    sortDirection   : 'asc',
                    params          : {}
                },
                {
                    fieldId         : 'entity',
                    sortDirection   : 'asc',
                    params          : {}
                }
            ]
        },
    }),

    myReports: makeReport('My time entries', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                  : SystemColumnType.TIME_REPORTED,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id: SystemColumnType.REPORT_DATE
            },
            {
                id: SystemColumnType.REPORT_WORK_ITEM
            },
            {
                id: SystemColumnType.REPORT_MESSAGE
            }
        ],
        grouping: {
            mode    : GroupingType.CUSTOM,
            fields  : [
                {
                    fieldId         : 'entity',
                    sortDirection   : 'asc',
                    params          : {}
                }
            ]
        },
    }, () => { // use a function to ensure 'gd' (global data) is available when used in Quick Add config
        return {
            userIds: [
                gd.user.id
            ]
        }
    }, {
        disableUsersFilter: true
    }),

    allTimeEntries: makeReport('All time entries', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                  : SystemColumnType.TIME_REPORTED,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id                  : SystemColumnType.REPORTED_BY,
                aggregateFunction   : AggregateFunctionType.USERS_UNION
            },
            {
                id: SystemColumnType.REPORT_DATE
            },
            {
                id: SystemColumnType.REPORT_WORK_ITEM
            },
        ],
        grouping: {
            mode: GroupingType.NO_GROUPING
        },
    }),

    custom: makeReport('Custom', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                  : SystemColumnType.TIME_REPORTED,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id                  : SystemColumnType.REPORTED_BY,
                aggregateFunction   : AggregateFunctionType.USERS_UNION
            },
            {
                id: SystemColumnType.REPORT_DATE
            },
        ],
    }),

    finance: makeReport('Finance', {
        ...TimeReportTable_defaultAppSettings,
        columns: [
            {
                id: SystemColumnType.TITLE
            },
            {
                id: SystemColumnType.REPORT_ID
            },
            {
                id: SystemColumnType.PROJECT
            },
            {
                id                  : SystemColumnType.TIME_REPORTED,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id                  : SystemColumnType.REPORTED_BY,
                aggregateFunction   : AggregateFunctionType.USERS_UNION
            },
            {
                id                  : SystemColumnType.REPORT_IS_BILLABLE,
                aggregateFunction   : AggregateFunctionType.CHECKED_TOTAL
            },
            {
                id                  : SystemColumnType.COST_INTERNAL,
                aggregateFunction   : AggregateFunctionType.SUM
            },
            {
                id                  : SystemColumnType.COST_EXTERNAL,
                aggregateFunction   : AggregateFunctionType.SUM
            }
        ],
        grouping: {
            mode    : GroupingType.HIERARCHY
        },
    }),
}
